import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, Clock, TrendingUp, Github, Slack, Bell } from "lucide-react";

export default function Dashboard() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-2xl font-heading font-bold">Healion.ai</h1>
            <nav className="hidden md:flex gap-6">
              <Link to="/dashboard" className="text-sm font-medium border-b-2 border-foreground pb-1">
                Dashboard
              </Link>
              <Link to="/errors" className="text-sm text-muted-foreground hover:text-foreground">
                Errors
              </Link>
              <Link to="/connections" className="text-sm text-muted-foreground hover:text-foreground">
                Connections
              </Link>
              <Link to="/analytics" className="text-sm text-muted-foreground hover:text-foreground">
                Analytics
              </Link>
              <Link to="/settings" className="text-sm text-muted-foreground hover:text-foreground">
                Settings
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm">
              Invite Team
            </Button>
            <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-medium">
              A
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Errors Fixed</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-heading font-bold">247</div>
            <p className="text-xs text-muted-foreground mt-1">+32 this week</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Acceptance Rate</span>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-heading font-bold">94%</div>
            <p className="text-xs text-muted-foreground mt-1">↑ 8% from last month</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Pending Review</span>
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="text-3xl font-heading font-bold">12</div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Failed Fixes</span>
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-3xl font-heading font-bold">8</div>
            <p className="text-xs text-muted-foreground mt-1">Needs attention</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Errors */}
          <Card className="lg:col-span-2 p-6 border-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold">Recent Errors</h2>
              <Link to="/errors">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </div>
            <div className="space-y-4">
              {[
                {
                  title: "TypeError: Cannot read property 'map' of undefined",
                  file: "src/components/UserList.tsx:42",
                  status: "fixed",
                  time: "2 hours ago",
                },
                {
                  title: "ReferenceError: userId is not defined",
                  file: "src/api/users.ts:128",
                  status: "pending",
                  time: "4 hours ago",
                },
                {
                  title: "UnhandledPromiseRejection: Network request failed",
                  file: "src/services/api.ts:56",
                  status: "reviewing",
                  time: "6 hours ago",
                },
              ].map((error, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <div className="flex-shrink-0 mt-1">
                    {error.status === "fixed" && <CheckCircle className="w-5 h-5 text-green-600" />}
                    {error.status === "pending" && <Clock className="w-5 h-5 text-yellow-600" />}
                    {error.status === "reviewing" && <Clock className="w-5 h-5 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm mb-1 truncate">{error.title}</p>
                    <p className="text-xs text-muted-foreground mb-2">{error.file}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        error.status === "fixed" ? "bg-green-600/10 text-green-600" :
                        error.status === "pending" ? "bg-yellow-600/10 text-yellow-600" :
                        "bg-blue-600/10 text-blue-600"
                      }`}>
                        {error.status}
                      </span>
                      <span className="text-xs text-muted-foreground">{error.time}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View Fix</Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Quick Actions */}
          <div className="space-y-6">
            <Card className="p-6 border-2">
              <h2 className="text-xl font-heading font-bold mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Button className="w-full justify-start" variant="outline">
                  <Github className="mr-2 w-4 h-4" />
                  View Open PRs
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Slack className="mr-2 w-4 h-4" />
                  Slack Notifications
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Bell className="mr-2 w-4 h-4" />
                  Configure Alerts
                </Button>
              </div>
            </Card>

            <Card className="p-6 border-2">
              <h2 className="text-xl font-heading font-bold mb-4">Connections</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Github className="w-4 h-4" />
                    <span className="text-sm">GitHub</span>
                  </div>
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                    Connected
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Slack className="w-4 h-4" />
                    <span className="text-sm">Slack</span>
                  </div>
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                    Connected
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    <span className="text-sm">Sentry</span>
                  </div>
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                    Connected
                  </span>
                </div>
              </div>
              <Link to="/connections">
                <Button variant="ghost" size="sm" className="w-full mt-4">
                  Manage Connections
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
